import { type PropsWithChildren, useEffect } from "react";

import axios, { type AxiosRequestConfig } from "axios";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { emitUserUnauthenticated } from "src/state/events.ts";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { routes } from "src/constants/routes.constants.ts";

import { logger } from "src/logger";
import { useSnackbar } from "src/App/App.hooks.ts";
import { LoginRouteSearchParams } from "src/constants/constants.ts";
import type { Router } from "@remix-run/router";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { queryClient } from "src/lib/react-query.tsx";
import { memoryStorage } from "@exonn/storage";

enum HttpErrorCodes {
  Unauthenticated = 401,
  CsrfTokenExpired = 419,
}

export type ExtendedAxiosRequestConfig = AxiosRequestConfig & {
  skipAuthRefresh?: boolean;
};

const REDIRECT_TO_ON_UNAUTHENTICATED_PATH = routes.root.root;

export const SessionProvider = ({
  children,
  router,
}: PropsWithChildren<{
  router: Router;
}>) => {
  const { t } = useTranslation();

  const api = useExonnApi();
  const snackbar = useSnackbar();

  useEffect(() => {
    const onUnauthenticated = (error: any) => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
      if (!error.response) {
        return Promise.reject(error);
      }

      const config = error.response.config as ExtendedAxiosRequestConfig;

      if (config.skipAuthRefresh) {
        return Promise.reject(error);
      }

      if (error.response.status === HttpErrorCodes.Unauthenticated) {
        emitUserUnauthenticated();

        // clearing cache
        queryClient.clear();
        memoryStorage.clear();
        const currentLocation =
          router.state.navigation.location || router.state.location;

        if (currentLocation.pathname === REDIRECT_TO_ON_UNAUTHENTICATED_PATH) {
          // to prevent infinite loop
          return Promise.reject(error);
        }

        const searchParams = new URLSearchParams();
        searchParams.set(LoginRouteSearchParams.From, window.location.pathname);

        logger.debug("session expired. redirecting user to login page");
        router.navigate({
          pathname: REDIRECT_TO_ON_UNAUTHENTICATED_PATH,
          search: searchParams.toString(),
        });
        snackbar.info({
          flash: true,
          message: t("session-expired", {
            ns: TranslationNamespaceEnum.Common,
          }),
        });
      }
      return Promise.reject(error);
    };

    const id = api.client.interceptors.response.use(null, onUnauthenticated);

    return () => api.client.interceptors.response.eject(id);
  }, [api, snackbar, router, t]);

  useEffect(() => {
    const id = createAuthRefreshInterceptor(
      api.client,
      () => {
        return api.auth.initializeCsrfCookie();
      },
      {
        statusCodes: [HttpErrorCodes.CsrfTokenExpired],
      },
    );
    return () => api.client.interceptors.response.eject(id);
  }, [api]);

  return <>{children}</>;
};
